import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getWebPushToken,
  getWebPushTokenIfGranted,
  onForegroundMessage,
  isFirebaseConfigured,
  getNotificationPermission,
  type PushPermission,
} from '../config/firebase';
import { notificationApi } from '../services/notificationApi';

type ForegroundPayload = { title?: string; body?: string; data?: Record<string, any> };

export interface PushRegistration {
  /** Current browser permission, or 'unsupported'. */
  permission: PushPermission;
  /** Whether this browser can do web push at all. */
  supported: boolean;
  /** Whether the Firebase web config env vars are present. */
  configured: boolean;
  /** True while a user-triggered enable request is in flight. */
  busy: boolean;
  /**
   * Prompt the admin for permission and register the FCM token. MUST be called from a
   * user gesture so the browser shows its prompt. Resolves with the resulting permission.
   */
  enablePush: () => Promise<PushPermission>;
}

/**
 * Owns FCM web-push for the logged-in admin.
 *
 *  - On login it SILENTLY re-syncs the token only when permission is already 'granted'
 *    (it never auto-prompts — the prompt is driven by <EnablePushButton />).
 *  - Foreground messages just run `onForeground` (e.g. refresh the bell); the Socket.IO
 *    handler already surfaces the in-app notification, and background push is shown by
 *    public/firebase-messaging-sw.js — so no duplicate Notification is raised here.
 *
 * No-op when Firebase web config env vars are missing — Socket.IO + the bell still work.
 */
export function usePushRegistration(
  isLoggedIn: boolean,
  onForeground?: (payload: ForegroundPayload) => void
): PushRegistration {
  const configured = isFirebaseConfigured();
  const [permission, setPermission] = useState<PushPermission>(() => getNotificationPermission());
  const [busy, setBusy] = useState(false);

  const registeredRef = useRef(false);
  const listenerSetRef = useRef(false);
  const unsubRef = useRef<() => void>(() => {});
  const onForegroundRef = useRef(onForeground);
  onForegroundRef.current = onForeground;

  const ensureForegroundListener = useCallback(async () => {
    if (listenerSetRef.current) return;
    listenerSetRef.current = true;
    unsubRef.current = await onForegroundMessage((payload) => {
      onForegroundRef.current?.(payload);
    });
  }, []);

  const registerToken = useCallback(
    async (token: string) => {
      await notificationApi.registerDeviceToken(token, 'web');
      registeredRef.current = true;
      await ensureForegroundListener();
    },
    [ensureForegroundListener]
  );

  // Reset the guard on logout so a re-login re-syncs.
  useEffect(() => {
    if (!isLoggedIn) registeredRef.current = false;
  }, [isLoggedIn]);

  // Silent re-sync on login — ONLY when permission already granted (no prompt).
  useEffect(() => {
    if (!isLoggedIn || !configured) return;
    if (registeredRef.current) return;

    let cancelled = false;
    (async () => {
      const perm = getNotificationPermission();
      if (!cancelled) setPermission(perm);
      if (perm !== 'granted') return; // wait for the button
      try {
        const token = await getWebPushTokenIfGranted();
        if (cancelled || !token) return;
        await registerToken(token);
      } catch (e) {
        console.warn('[push] silent registration failed:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, configured, registerToken]);

  // Detach the foreground listener on unmount.
  useEffect(
    () => () => {
      try {
        unsubRef.current?.();
      } catch {
        /* ignore */
      }
    },
    []
  );

  const enablePush = useCallback(async (): Promise<PushPermission> => {
    if (!configured) return getNotificationPermission();
    setBusy(true);
    try {
      const token = await getWebPushToken(); // prompts when permission is 'default'
      const perm = getNotificationPermission();
      setPermission(perm);
      if (token) await registerToken(token);
      return perm;
    } catch (e) {
      console.warn('[push] enable failed:', e);
      const perm = getNotificationPermission();
      setPermission(perm);
      return perm;
    } finally {
      setBusy(false);
    }
  }, [configured, registerToken]);

  return {
    permission,
    supported: permission !== 'unsupported',
    configured,
    busy,
    enablePush,
  };
}
